import React, { useRef } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import logo from '../../../assets/logos/logo.png';
import './PaymentReceiptTemplate.css';

const convertToWords = (num) => {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
  const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const thousands = ['', 'Thousand', 'Million', 'Billion'];

  if (num === 0) return 'Zero';

  const convertHundreds = (n) => {
    let result = '';
    if (n > 99) {
      result += ones[Math.floor(n / 100)] + ' Hundred ';
      n %= 100;
    }
    if (n > 19) {
      result += tens[Math.floor(n / 10)] + ' ';
      n %= 10;
    } else if (n > 9) {
      result += teens[n - 10] + ' ';
      return result;
    }
    if (n > 0) {
      result += ones[n] + ' ';
    }
    return result;
  };

  let result = '';
  let thousandIndex = 0;
  let numCopy = Math.floor(num);

  while (numCopy > 0) {
    if (numCopy % 1000 !== 0) {
      result = convertHundreds(numCopy % 1000) + thousands[thousandIndex] + ' ' + result;
    }
    numCopy = Math.floor(numCopy / 1000);
    thousandIndex++;
  }

  return result.trim();
};

const PaymentReceiptTemplate = ({ receipt, onClose }) => {
  const receiptRef = useRef(null);
  
  const generatePDF = async () => {
    if (!receiptRef.current) return;

    try {
      // Try backend API first when available
      try {
        const response = await fetch('/api/receipts/generate-pdf', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ receipt }),
        });

        const contentType = response.headers.get('content-type') || '';
        if (response.ok && contentType.toLowerCase().includes('pdf')) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `Skript_Payment_Receipt_${receipt.receiptNumber}.pdf`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
          return;
        }
      } catch (apiError) {
        console.warn('Backend PDF generation failed, falling back to client-side:', apiError);
      }

      // Client-side: html2canvas + jsPDF (JPEG keeps file size down and avoids corrupt PDFs)
      const canvas = await html2canvas(receiptRef.current, {
        scale: 1.5,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        logging: false,
      });

      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // JPEG is smaller and more reliable in jsPDF than PNG for large content
      const imgData = canvas.toDataURL('image/jpeg', 0.92);
      const pdf = new jsPDF('p', 'mm', 'a4');

      let position = 0;
      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
      let heightLeft = imgHeight - pageHeight;

      while (heightLeft > 0) {
        position -= pageHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`Skript_Payment_Receipt_${receipt.receiptNumber}.pdf`);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Error generating PDF. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const amount = parseFloat(receipt.amount || 0);
  const amountInWords = convertToWords(amount);

  return (
    <div className="receipt-template-overlay">
      <div className="receipt-template-container">
        <div className="receipt-template-header">
          <h2>Payment Receipt Preview</h2>
          <div className="receipt-template-actions">
            <button onClick={generatePDF} className="generate-pdf-btn">
              📄 Generate PDF
            </button>
            <button onClick={onClose} className="close-receipt-btn">
              ✕ Close
            </button>
          </div>
        </div>
        
        <div className="receipt-preview-container">
          <div ref={receiptRef} className="receipt-template">
            {/* Header */}
            <div className="receipt-header">
              <div className="company-info">
                <img src={logo} alt="Skript Tech Logo" className="company-logo" />
                <div className="company-details">
                  <h1 className="company-name">Skript Tech</h1>
                  <p className="company-tagline">Empowering Digital Solutions</p>
                  <div className="company-contact">
                    <p>Beirut Malaab, Lebanon</p>
                    <p>support@skriptdev.com</p>
                    <p>www.skriptdev.com</p>
                    <p>Registration: 4039386</p>
                  </div>
                </div>
              </div>
              
              <div className="receipt-meta">
                <div className="receipt-number">
                  <p className="receipt-id">Receipt No: {receipt.receiptNumber || 'N/A'}</p>
                </div>
                <div className="receipt-date">
                  <p><strong>Date:</strong> {formatDate(receipt.date)}</p>
                </div>
              </div>
            </div>

            {/* Recipient Information */}
            <div className="client-section">
              <h3>Receipt From:</h3>
              <div className="client-info">
                <p className="client-name">{receipt.recipientName || 'N/A'}</p>
                {receipt.recipientAddress && <p className="client-address">{receipt.recipientAddress}</p>}
                {receipt.recipientEmail && <p className="client-email">{receipt.recipientEmail}</p>}
                {receipt.recipientPhone && <p className="client-phone">{receipt.recipientPhone}</p>}
              </div>
            </div>

            {/* Payment Description */}
            <div className="payment-description-section">
              <div className="payment-description">
                <p className="description-text">
                  We have received from <strong>{receipt.recipientName || 'N/A'}</strong> the amount of <strong>${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                </p>
                <p className="amount-in-words">
                  <strong>{amountInWords} US Dollars Only</strong>
                </p>
              </div>
            </div>

            {/* Notes Section */}
            {receipt.notes && (
              <div className="notes-section">
                <p><strong>Notes:</strong></p>
                <p>{receipt.notes}</p>
              </div>
            )}

            {/* Footer */}
            <div className="invoice-footer">
              <p className="thank-you">Thank you for your payment!</p>
              <p className="footer-note">This receipt confirms the payment has been received and processed.</p>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentReceiptTemplate;

